import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { MessageCircle, X, Send, Users, Minimize2, Maximize2, GripHorizontal, Anchor } from "lucide-react";
import ImageUpload from "@/components/ImageUpload";
import { updateBadgeSource } from "@/utils/notifications";
import { getImageUrl } from "@/utils/imageUtils";
import RichContent from "@/components/RichContent";
import { toast } from "sonner";
import { BACKEND_URL } from '@/utils/apiConfig';

const API = `${BACKEND_URL}/api`;
// Derive WS URL from current page origin so it works after domain changes
const getWsUrl = () => {
  const origin = window.location.origin;
  return origin.replace("https://", "wss://").replace("http://", "ws://");
};

const REACTION_EMOJIS = ["🐟", "🐠", "🦈", "🐡", "🐙", "👍", "❤️", "😂"];

const FloatingChat = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [rooms, setRooms] = useState([]);
  const [activeRoom, setActiveRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [pendingImage, setPendingImage] = useState(null);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [showOnlineList, setShowOnlineList] = useState(false);
  const [loading, setLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showReactionPicker, setShowReactionPicker] = useState(null);
  const [wsConnected, setWsConnected] = useState(false);
  const [allUsers, setAllUsers] = useState({});
  const [typingUsers, setTypingUsers] = useState([]); // [{id, name}]
  
  // Mention autocomplete state
  const [mentionQuery, setMentionQuery] = useState("");
  const [showMentionDropdown, setShowMentionDropdown] = useState(false);
  const [mentionCursorPos, setMentionCursorPos] = useState(0);
  const [mentionUsers, setMentionUsers] = useState([]);
  
  // Dragging state - used for open/minimized chat window
  const [position, setPosition] = useState({ x: null, y: null });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isMobile, setIsMobile] = useState(false);
  
  // Button drag state (separate from chat window drag)
  const [buttonPos, setButtonPos] = useState({ x: null, y: null });
  const [isButtonDragging, setIsButtonDragging] = useState(false);
  const [buttonDragOffset, setButtonDragOffset] = useState({ x: 0, y: 0 });
  const [buttonDragMoved, setButtonDragMoved] = useState(false);
  const buttonRef = useRef(null);
  
  const wsRef = useRef(null);
  const messagesEndRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const chatWindowRef = useRef(null);
  const inputRef = useRef(null);
  const typingTimeoutsRef = useRef({}); // user_id -> timeout
  const lastTypingSentRef = useRef(0); // throttle outgoing typing events
  const wsRetryCountRef = useRef(0); // exponential backoff counter
  const MAX_WS_RETRIES = 10;
  const heartbeatRef = useRef(null);
  const pongTimeoutRef = useRef(null);
  const messagesRef = useRef([]);
  const activeRoomRef = useRef(null);
  
  // Refs to avoid stale closures in WebSocket handler
  const isOpenRef = useRef(isOpen);
  const isMinimizedRef = useRef(isMinimized);
  
  // Mobile viewport height (tracks visual viewport for keyboard avoidance)
  const [mobileViewportHeight, setMobileViewportHeight] = useState(window.innerHeight);
  const [mobileViewportOffset, setMobileViewportOffset] = useState(0);

  // Keep refs in sync with state for WebSocket handler
  useEffect(() => { isOpenRef.current = isOpen; }, [isOpen]);
  useEffect(() => { isMinimizedRef.current = isMinimized; }, [isMinimized]);
  useEffect(() => { messagesRef.current = messages; }, [messages]);
  useEffect(() => { activeRoomRef.current = activeRoom; }, [activeRoom]);

  // Append messages that aren't already in the list (WS + API + polling can overlap)
  const mergeMessages = (incoming) => {
    const list = Array.isArray(incoming) ? incoming : [incoming];
    setMessages(prev => {
      const seen = new Set(prev.map(m => m.id));
      const fresh = list.filter(m => m && !seen.has(m.id));
      return fresh.length ? [...prev, ...fresh] : prev;
    });
  };

  // Detect mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768 || 'ontouchstart' in window);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Track visual viewport for mobile keyboard avoidance
  useEffect(() => {
    if (!isMobile || !isOpen) return;
    
    const updateViewport = () => {
      if (window.visualViewport) {
        setMobileViewportHeight(window.visualViewport.height);
        setMobileViewportOffset(window.visualViewport.offsetTop);
      } else {
        setMobileViewportHeight(window.innerHeight);
        setMobileViewportOffset(0);
      }
    };
    
    updateViewport();
    
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', updateViewport);
      window.visualViewport.addEventListener('scroll', updateViewport);
    }
    window.addEventListener('resize', updateViewport);
    
    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', updateViewport);
        window.visualViewport.removeEventListener('scroll', updateViewport);
      }
      window.removeEventListener('resize', updateViewport);
    };
  }, [isMobile, isOpen]);

  // Set initial position when opening
  useEffect(() => {
    if (isOpen && position.x === null) {
      // Default position: bottom-left, avoiding watermark
      setPosition({
        x: 16,
        y: window.innerHeight - 530 // 450px height + 80px from bottom
      });
    }
  }, [isOpen]);

  useEffect(() => {
    const userData = localStorage.getItem("user") || sessionStorage.getItem("user");
    if (userData) {
      const parsedUser = JSON.parse(userData);
      if (parsedUser.role !== "dive_center") {
        setUser(parsedUser);
      }
    }
  }, []);

  useEffect(() => {
    if (user) {
      fetchRooms();
      fetchAllUsersForMentions();
      
      // Refresh users list every 5 minutes to catch new members
      const refreshInterval = setInterval(fetchAllUsersForMentions, 5 * 60 * 1000);
      
      // Listen for trip vote events to refresh rooms
      const handleRoomRefresh = () => fetchRooms();
      window.addEventListener('refreshChatRooms', handleRoomRefresh);
      
      return () => {
        clearInterval(refreshInterval);
        window.removeEventListener('refreshChatRooms', handleRoomRefresh);
      };
    }
  }, [user]);

  // Fetch all users for mention autocomplete
  const fetchAllUsersForMentions = async () => {
    try {
      const token = getToken();
      if (!token) return;
      
      const response = await axios.get(`${API}/users/chat-mentions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Convert to object keyed by user id
      const usersMap = {};
      response.data.forEach(u => {
        usersMap[u.id] = {
          id: u.id,
          name: u.name || u.username || 'Unknown',
          profile_pic: u.profile_pic
        };
      });
      setAllUsers(prev => ({ ...prev, ...usersMap }));
    } catch (error) {
      console.error("Failed to fetch users for mentions:", error);
    }
  };

  // Update PWA badge when chat unread count changes
  useEffect(() => {
    updateBadgeSource('chat', unreadCount);
  }, [unreadCount]);

  // Connect WebSocket when we have an active room and user (regardless of chat being open)
  useEffect(() => {
    if (activeRoom && user) {
      wsRetryCountRef.current = 0; // Reset retries for new room
      connectWebSocket();
      fetchInitialUnreadCount();
    }
    
    return () => {
      stopHeartbeat();
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [activeRoom, user]);

  // Mobile browsers silently kill sockets in the background: re-sync whenever we come back
  useEffect(() => {
    if (!activeRoom || !user) return;
    const resync = () => {
      if (document.visibilityState === "hidden") return;
      const open = wsRef.current && wsRef.current.readyState === WebSocket.OPEN;
      if (!open) {
        wsRetryCountRef.current = 0;
        if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
        if (wsRef.current) { try { wsRef.current.close(); } catch (e) { /* ignore */ } }
        connectWebSocket();
      }
      if (isOpenRef.current && !isMinimizedRef.current) fetchNewMessages();
      else fetchInitialUnreadCount();
    };
    document.addEventListener("visibilitychange", resync);
    window.addEventListener("focus", resync);
    window.addEventListener("online", resync);
    window.addEventListener("pageshow", resync);
    return () => {
      document.removeEventListener("visibilitychange", resync);
      window.removeEventListener("focus", resync);
      window.removeEventListener("online", resync);
      window.removeEventListener("pageshow", resync);
    };
  }, [activeRoom, user]);

  // Polling fallback while the socket is down (chat open → messages, closed → unread count)
  useEffect(() => {
    if (!activeRoom || !user || wsConnected) return;
    const id = setInterval(() => {
      if (document.visibilityState === "hidden") return;
      if (isOpenRef.current && !isMinimizedRef.current) fetchNewMessages();
      else fetchInitialUnreadCount();
    }, 8000);
    return () => clearInterval(id);
  }, [activeRoom, user, wsConnected]);

  // Fetch messages when chat is opened
  useEffect(() => {
    if (isOpen && activeRoom) {
      fetchMessages();
    }
  }, [isOpen, activeRoom]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Fetch user info for unknown reaction users
  const fetchUserInfo = async (userId) => {
    try {
      const token = localStorage.getItem("token") || sessionStorage.getItem("token");
      const response = await axios.get(`${API}/users/${userId}/public`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      return response.data;
    } catch (error) {
      console.error(`Failed to fetch user info for ${userId}:`, error);
      return null;
    }
  };

  useEffect(() => {
    const userCache = { ...allUsers };
    const unknownUserIds = new Set();
    
    messages.forEach(msg => {
      // Add message sender to cache
      if (!userCache[msg.user_id]) {
        userCache[msg.user_id] = {
          id: msg.user_id,
          name: msg.user_name,
          profile_pic: msg.user_profile_pic
        };
      }
      
      // Collect unknown user IDs from reactions
      if (msg.reactions) {
        Object.values(msg.reactions).forEach(userIds => {
          userIds.forEach(userId => {
            if (!userCache[userId]) {
              unknownUserIds.add(userId);
            }
          });
        });
      }
    });
    
    if (user && !userCache[user.id]) {
      userCache[user.id] = {
        id: user.id,
        name: user.name,
        profile_pic: user.profile_pic
      };
    }
    
    // Fetch info for unknown users from reactions
    if (unknownUserIds.size > 0) {
      unknownUserIds.forEach(async (userId) => {
        const userInfo = await fetchUserInfo(userId);
        if (userInfo) {
          setAllUsers(prev => ({
            ...prev,
            [userId]: {
              id: userId,
              name: userInfo.name || userInfo.username,
              profile_pic: userInfo.profile_picture
            }
          }));
        }
      });
    }
    
    setAllUsers(userCache);
  }, [messages, user]);

  // Drag handlers for chat window (both open and minimized states)
  const handleMouseDown = useCallback((e) => {
    if (isMobile) return;
    
    // Only drag from header area
    if (e.target.closest('[data-drag-handle]')) {
      setIsDragging(true);
      const rect = chatWindowRef.current.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
      e.preventDefault();
    }
  }, [isMobile]);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging || isMobile) return;
    
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;
    
    // Dragging the open or minimized chat window
    const chatWidth = chatWindowRef.current?.offsetWidth || 360;
    const chatHeight = chatWindowRef.current?.offsetHeight || (isMinimized ? 48 : 450);
    
    let newX = e.clientX - dragOffset.x;
    let newY = e.clientY - dragOffset.y;
    
    // Keep within viewport
    newX = Math.max(0, Math.min(newX, windowWidth - chatWidth));
    newY = Math.max(0, Math.min(newY, windowHeight - chatHeight));
    
    setPosition({ x: newX, y: newY });
  }, [isDragging, dragOffset, isMobile, isMinimized]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Add/remove mouse event listeners
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // Button drag handlers (for the floating bubble when chat is closed)
  const handleButtonMouseDown = useCallback((e) => {
    if (isMobile) return;
    setIsButtonDragging(true);
    setButtonDragMoved(false);
    const rect = buttonRef.current.getBoundingClientRect();
    setButtonDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
    e.preventDefault();
  }, [isMobile]);

  const handleButtonTouchStart = useCallback((e) => {
    const touch = e.touches[0];
    setIsButtonDragging(true);
    setButtonDragMoved(false);
    const rect = buttonRef.current.getBoundingClientRect();
    setButtonDragOffset({
      x: touch.clientX - rect.left,
      y: touch.clientY - rect.top
    });
    // Prevent page scroll while dragging
    e.preventDefault();
  }, []);

  const handleButtonMove = useCallback((clientX, clientY) => {
    if (!isButtonDragging) return;
    setButtonDragMoved(true);
    const newX = clientX - buttonDragOffset.x;
    const newY = clientY - buttonDragOffset.y;
    const maxX = window.innerWidth - 48;
    const maxY = window.innerHeight - 48;
    setButtonPos({
      x: Math.max(0, Math.min(newX, maxX)),
      y: Math.max(0, Math.min(newY, maxY))
    });
  }, [isButtonDragging, buttonDragOffset]);

  const handleButtonMouseMove = useCallback((e) => {
    handleButtonMove(e.clientX, e.clientY);
  }, [handleButtonMove]);

  const handleButtonTouchMove = useCallback((e) => {
    const touch = e.touches[0];
    handleButtonMove(touch.clientX, touch.clientY);
    e.preventDefault();
  }, [handleButtonMove]);

  const handleButtonEnd = useCallback(() => {
    setIsButtonDragging(false);
  }, []);

  useEffect(() => {
    if (isButtonDragging) {
      window.addEventListener('mousemove', handleButtonMouseMove);
      window.addEventListener('mouseup', handleButtonEnd);
      window.addEventListener('touchmove', handleButtonTouchMove, { passive: false });
      window.addEventListener('touchend', handleButtonEnd);
      return () => {
        window.removeEventListener('mousemove', handleButtonMouseMove);
        window.removeEventListener('mouseup', handleButtonEnd);
        window.removeEventListener('touchmove', handleButtonTouchMove);
        window.removeEventListener('touchend', handleButtonEnd);
      };
    }
  }, [isButtonDragging, handleButtonMouseMove, handleButtonTouchMove, handleButtonEnd]);

  const getToken = () => localStorage.getItem("token") || sessionStorage.getItem("token");

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const fetchRooms = async () => {
    try {
      const response = await axios.get(`${API}/chat/rooms`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      const newRooms = response.data;
      setRooms(newRooms);
      
      // If active room is no longer in the list (e.g. unvoted from trip), switch to global
      if (activeRoom && !newRooms.find(r => r.id === activeRoom.id)) {
        const globalRoom = newRooms.find(r => r.is_global);
        if (globalRoom) setActiveRoom(globalRoom);
      }
      // If no active room, default to global
      if (!activeRoom) {
        const globalRoom = newRooms.find(r => r.is_global);
        if (globalRoom) setActiveRoom(globalRoom);
      }
    } catch (err) {
      console.error("Failed to fetch rooms:", err);
    }
  };

  const fetchMessages = async () => {
    if (!activeRoom) return;
    
    try {
      setLoading(true);
      setTypingUsers([]); // Clear typing indicators on room switch
      const response = await axios.get(`${API}/chat/rooms/${activeRoom.id}/messages?limit=50`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setMessages(response.data);
      setUnreadCount(0);
      // Mark room as read on server
      try {
        await axios.put(`${API}/chat/rooms/${activeRoom.id}/mark-read`, {}, {
          headers: { Authorization: `Bearer ${getToken()}` }
        });
      } catch (e) { /* non-critical */ }
    } catch (err) {
      console.error("Failed to fetch messages:", err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch initial unread count when room is set (for messages missed while offline)
  const fetchInitialUnreadCount = async () => {
    if (!activeRoom || isOpenRef.current) return;
    try {
      const token = getToken();
      if (!token) return;
      // Server-side last_read is used automatically by the backend
      const response = await axios.get(`${API}/chat/rooms/${activeRoom.id}/unread-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.unread_count > 0) {
        setUnreadCount(response.data.unread_count);
      }
    } catch (err) {
      console.error("Failed to fetch initial unread count:", err);
    }
  };

  // Pull anything newer than what we have (gap-fill after reconnect / polling fallback)
  const fetchNewMessages = async () => {
    const room = activeRoomRef.current;
    if (!room) return;
    try {
      const response = await axios.get(`${API}/chat/rooms/${room.id}/messages?limit=50`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      mergeMessages(response.data);
    } catch (err) { /* transient */ }
  };

  const stopHeartbeat = () => {
    if (heartbeatRef.current) clearInterval(heartbeatRef.current);
    if (pongTimeoutRef.current) clearTimeout(pongTimeoutRef.current);
    heartbeatRef.current = null;
    pongTimeoutRef.current = null;
  };

  // Detect half-dead sockets (common on mobile): ping every 25s, expect pong within 10s
  const startHeartbeat = (ws) => {
    stopHeartbeat();
    heartbeatRef.current = setInterval(() => {
      if (ws.readyState !== WebSocket.OPEN) return;
      try { ws.send(JSON.stringify({ type: "ping" })); } catch (e) { return; }
      if (pongTimeoutRef.current) clearTimeout(pongTimeoutRef.current);
      pongTimeoutRef.current = setTimeout(() => {
        setWsConnected(false);
        try { ws.close(); } catch (e) { /* ignore */ }
      }, 10000);
    }, 25000);
  };

  const connectWebSocket = () => {
    if (!activeRoom || !user) return;
    
    const token = getToken();
    const wsUrl = `${getWsUrl()}/api/ws/chat/${activeRoom.id}?token=${token}`;
    
    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      
      ws.onopen = () => {
        setWsConnected(true);
        wsRetryCountRef.current = 0; // Reset on successful connection
        startHeartbeat(ws);
        // Fill any gap from the time we were disconnected
        if (isOpenRef.current && !isMinimizedRef.current) fetchNewMessages();
        else fetchInitialUnreadCount();
      };
      
      ws.onmessage = (event) => {
        const data = JSON.parse(event.data);
        
        switch (data.type) {
          case "pong":
            if (pongTimeoutRef.current) clearTimeout(pongTimeoutRef.current);
            pongTimeoutRef.current = null;
            break;
          case "new_message": {
            const isNew = !messagesRef.current.some(m => m.id === data.message?.id);
            mergeMessages(data.message);
            // Use refs to get current state (avoids stale closure)
            // Only count messages from OTHER users as unread
            if (isNew && (!isOpenRef.current || isMinimizedRef.current) && data.message?.user_id !== user?.id) {
              setUnreadCount(prev => prev + 1);
            }
            // Clear typing indicator for this user (they sent a message)
            if (data.message?.user_id) {
              setTypingUsers(prev => prev.filter(u => u.id !== data.message.user_id));
            }
            break;
          }
          case "user_joined":
          case "user_left":
            setOnlineUsers(data.online_users || []);
            // Clear typing for user who left
            if (data.type === "user_left" && data.user?.id) {
              setTypingUsers(prev => prev.filter(u => u.id !== data.user.id));
            }
            break;
          case "user_typing":
            if (data.user?.id !== user?.id) {
              setTypingUsers(prev => {
                const exists = prev.find(u => u.id === data.user.id);
                if (!exists) return [...prev, { id: data.user.id, name: data.user.name }];
                return prev;
              });
              // Auto-clear after 3 seconds
              if (typingTimeoutsRef.current[data.user.id]) {
                clearTimeout(typingTimeoutsRef.current[data.user.id]);
              }
              typingTimeoutsRef.current[data.user.id] = setTimeout(() => {
                setTypingUsers(prev => prev.filter(u => u.id !== data.user.id));
                delete typingTimeoutsRef.current[data.user.id];
              }, 3000);
            }
            break;
          case "reaction_update":
            setMessages(prev => prev.map(m => 
              m.id === data.message_id ? { ...m, reactions: data.reactions } : m
            ));
            break;
          case "chat_cleared":
            setMessages([]);
            setUnreadCount(0);
            break;
          case "message_deleted":
            setMessages(prev => prev.map(m =>
              m.id === data.message_id ? { ...m, is_deleted: true, content: "[Message deleted]" } : m
            ));
            break;
          default:
            break;
        }
      };
      
      ws.onclose = () => {
        setWsConnected(false);
        setOnlineUsers([]);
        setShowOnlineList(false);
        stopHeartbeat();
        if (wsRef.current !== ws) return; // superseded by a newer socket
        if (activeRoom && user && wsRetryCountRef.current < MAX_WS_RETRIES) {
          const delay = Math.min(3000 * Math.pow(2, wsRetryCountRef.current), 60000);
          wsRetryCountRef.current += 1;
          reconnectTimeoutRef.current = setTimeout(() => {
            connectWebSocket();
          }, delay);
        }
      };
      
      ws.onerror = () => {
        setWsConnected(false);
      };
    } catch (err) {
      console.error("Failed to connect WebSocket:", err);
    }
  };

  // Always persist via REST (reliable even on a half-dead socket); WS broadcast is deduped by id
  const sendMessage = (e) => {
    e.preventDefault();
    if (!newMessage.trim() && !pendingImage) return;
    setShowMentionDropdown(false);
    sendMessageViaAPI();
  };

  // Handle mention autocomplete
  const handleInputChange = (e) => {
    const value = e.target.value;
    const cursorPos = e.target.selectionStart;
    setNewMessage(value);
    
    // Send typing indicator (throttled to once per 2 seconds)
    if (value.trim() && wsRef.current?.readyState === WebSocket.OPEN) {
      const now = Date.now();
      if (now - lastTypingSentRef.current > 2000) {
        wsRef.current.send(JSON.stringify({ type: "typing" }));
        lastTypingSentRef.current = now;
      }
    }
    
    // Find the word being typed at cursor position
    const beforeCursor = value.slice(0, cursorPos);
    const mentionMatch = beforeCursor.match(/@(\w*)$/);
    
    if (mentionMatch) {
      const query = mentionMatch[1].toLowerCase();
      setMentionQuery(query);
      setMentionCursorPos(cursorPos);
      
      // Filter users based on query
      const users = Object.values(allUsers).filter(u => 
        u.name?.toLowerCase().includes(query) || 
        u.id?.toLowerCase().includes(query)
      ).slice(0, 5);
      
      setMentionUsers(users);
      setShowMentionDropdown(users.length > 0);
    } else {
      setShowMentionDropdown(false);
    }
  };

  const selectMention = (selectedUser) => {
    // Replace the @query with @username
    const beforeAt = newMessage.slice(0, mentionCursorPos - mentionQuery.length - 1);
    const afterCursor = newMessage.slice(mentionCursorPos);
    const mention = `@${selectedUser.name} `;
    
    setNewMessage(beforeAt + mention + afterCursor);
    setShowMentionDropdown(false);
    inputRef.current?.focus();
  };

  const sendMessageViaAPI = async () => {
    if (!activeRoom || (!newMessage.trim() && !pendingImage)) return;
    
    try {
      // Extract mentioned user IDs from the message
      const mentionedIds = [];
      const mentionRegex = /@([^@\s]+(?:\s+[^@\s]+)?)/g;
      let match;
      while ((match = mentionRegex.exec(newMessage)) !== null) {
        const mentionedName = match[1].trim();
        // Find user by name in allUsers
        const mentionedUser = Object.values(allUsers).find(
          u => u.name?.toLowerCase() === mentionedName.toLowerCase()
        );
        if (mentionedUser && !mentionedIds.includes(mentionedUser.id)) {
          mentionedIds.push(mentionedUser.id);
        }
      }
      
      const response = await axios.post(
        `${API}/chat/rooms/${activeRoom.id}/messages`,
        { 
          content: newMessage.trim(), 
          image_url: pendingImage,
          mentions: mentionedIds
        },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      
      mergeMessages(response.data);
      setNewMessage("");
      setPendingImage(null);
    } catch (err) {
      console.error("Failed to send message:", err);
      toast.error("Message not sent — please try again");
    }
  };

  const handleReaction = async (messageId, emoji) => {
    try {
      const response = await axios.post(
        `${API}/chat/messages/${messageId}/react`,
        { emoji },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      setMessages(prev => prev.map(m => 
        m.id === messageId ? { ...m, reactions: response.data.reactions } : m
      ));
      setShowReactionPicker(null);
    } catch (err) {
      console.error("Failed to react:", err);
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    if (date.toDateString() === today.toDateString()) {
      return "Today";
    }
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  const getUserInfo = (userId) => {
    return allUsers[userId] || { name: "?", profile_pic: null };
  };

  const renderReactionWithAvatars = (emoji, userIds, messageId) => {
    const maxAvatars = 3;
    const displayUsers = userIds.slice(0, maxAvatars);
    const remainingCount = userIds.length - maxAvatars;
    
    return (
      <button
        key={emoji}
        onClick={() => handleReaction(messageId, emoji)}
        className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs transition-colors ${
          userIds.includes(user?.id)
            ? "bg-cyan-100 border border-cyan-300"
            : "bg-gray-100 hover:bg-gray-200 border border-transparent"
        }`}
        title={userIds.map(id => getUserInfo(id).name).join(", ")}
      >
        <span className="text-sm">{emoji}</span>
        <div className="flex -space-x-1">
          {displayUsers.map((userId, idx) => {
            const userInfo = getUserInfo(userId);
            return userInfo.profile_pic ? (
              <img
                key={userId}
                src={getImageUrl(userInfo.profile_pic)}
                alt=""
                className="w-4 h-4 rounded-full border border-white object-cover"
                style={{ zIndex: maxAvatars - idx }}
              />
            ) : (
              <div
                key={userId}
                className="w-4 h-4 rounded-full bg-gradient-to-br from-cyan-400 to-blue-500 border border-white flex items-center justify-center text-white text-[8px]"
                style={{ zIndex: maxAvatars - idx }}
              >
                {userInfo.name?.charAt(0) || "?"}
              </div>
            );
          })}
        </div>
        {remainingCount > 0 && (
          <span className="text-gray-500 text-[10px]">+{remainingCount}</span>
        )}
      </button>
    );
  };

  if (!user) return null;

  // Calculate window dimensions
  const chatWindowWidth = isMobile ? '100%' : 'min(360px, calc(100vw - 32px))';
  const chatWindowHeight = isMinimized ? '48px' : (isMobile ? '100%' : 'min(450px, calc(100vh - 100px))');

  const chatContent = (
    <>
      {/* Floating Chat Button - Draggable */}
      {!isOpen && (
        <button
          ref={buttonRef}
          onMouseDown={handleButtonMouseDown}
          onTouchStart={handleButtonTouchStart}
          onClick={() => {
            if (!buttonDragMoved) {
              setIsOpen(true);
              setUnreadCount(0);
            }
          }}
          className={`fixed w-12 h-12 rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 text-white shadow-lg hover:shadow-xl transition-shadow flex items-center justify-center z-[10100] ${isButtonDragging ? 'cursor-grabbing' : 'cursor-pointer'}`}
          style={buttonPos.x !== null ? { left: `${buttonPos.x}px`, top: `${buttonPos.y}px` } : { bottom: '80px', right: '16px' }}
          data-testid="floating-chat-btn"
        >
          <MessageCircle className="w-5 h-5" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-xs flex items-center justify-center">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div 
          ref={chatWindowRef}
          className={`fixed bg-white rounded-lg shadow-2xl border flex flex-col overflow-hidden z-[10100] ${
            isDragging ? 'cursor-grabbing select-none' : ''
          } ${isMobile ? 'rounded-none' : ''}`}
          style={isMobile ? { 
            top: `${mobileViewportOffset}px`,
            left: 0,
            width: '100%',
            height: `${mobileViewportHeight}px`
          } : { 
            left: `${position.x}px`,
            top: `${position.y}px`,
            width: chatWindowWidth,
            height: chatWindowHeight
          }}
          data-testid="chat-window"
        >
          {/* Header - Drag Handle */}
          <div 
            className={`flex items-center justify-between px-3 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white flex-shrink-0 ${
              !isMobile ? 'cursor-grab' : ''
            } ${isDragging ? 'cursor-grabbing' : ''}`}
            data-drag-handle
            onMouseDown={handleMouseDown}
          >
            <div className="flex items-center gap-2 flex-1 min-w-0">
              {/* Drag indicator for desktop */}
              {!isMobile && (
                <GripHorizontal className="w-4 h-4 opacity-50 flex-shrink-0" />
              )}
              <MessageCircle className="w-4 h-4 flex-shrink-0" />
              <span className="font-semibold text-sm truncate">
                {activeRoom?.name || "Live Chat"}
              </span>
              {!isMinimized && onlineUsers.length > 0 && (
                <button
                  type="button"
                  onClick={() => setShowOnlineList(v => !v)}
                  className="flex items-center gap-1 text-xs bg-white/20 hover:bg-white/30 px-2 py-0.5 rounded-full flex-shrink-0 transition-colors"
                  title="Who's online"
                  data-testid="online-count-btn"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                  <Users className="w-3 h-3" />
                  {onlineUsers.length}
                </button>
              )}
            </div>
            <div className="flex items-center flex-shrink-0 ml-2">
              {!isMobile && (
                <button
                  onClick={() => setIsMinimized(!isMinimized)}
                  className="p-1.5 hover:bg-white/20 rounded"
                >
                  {isMinimized ? <Maximize2 className="w-4 h-4" /> : <Minimize2 className="w-4 h-4" />}
                </button>
              )}
              <button
                onClick={() => {
                  setIsOpen(false);
                  // Mark room as read on server when closing chat
                  if (activeRoom && user) {
                    axios.put(`${API}/chat/rooms/${activeRoom.id}/mark-read`, {}, {
                      headers: { Authorization: `Bearer ${getToken()}` }
                    }).catch(() => {});
                  }
                }}
                className="p-1.5 hover:bg-white/20 rounded"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {!isMinimized && (
            <>
              {/* Room Tabs */}
              {rooms.length > 1 && (
                <div className="flex gap-1 p-2 border-b overflow-x-auto flex-shrink-0">
                  {rooms.map(room => (
                    <button
                      key={room.id}
                      onClick={() => setActiveRoom(room)}
                      data-testid={`chat-room-tab-${room.id}`}
                      className={`px-3 py-1 rounded-full text-sm whitespace-nowrap transition-colors flex items-center gap-1 ${
                        activeRoom?.id === room.id
                          ? room.trip_id 
                            ? "bg-emerald-500 text-white"
                            : "bg-cyan-500 text-white"
                          : room.trip_id
                            ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                            : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                      }`}
                    >
                      {room.trip_id && <Anchor className="w-3 h-3" />}
                      {room.name}
                    </button>
                  ))}
                </div>
              )}

              {/* Who's online */}
              {showOnlineList && (
                <div className="px-3 py-2 border-b bg-green-50/60 flex items-center gap-2 overflow-x-auto" data-testid="online-list">
                  <span className="text-[11px] font-semibold text-green-700 flex-shrink-0">Online now:</span>
                  {onlineUsers.map(u => (
                    <button
                      key={u.id}
                      type="button"
                      onClick={() => { setIsOpen(false); navigate(`/profile/${u.id}`); }}
                      className="flex items-center gap-1.5 bg-white border border-green-200 rounded-full pl-0.5 pr-2 py-0.5 flex-shrink-0 hover:border-green-400 transition-colors"
                      data-testid={`online-user-${u.id}`}
                    >
                      {u.profile_pic ? (
                        <img src={getImageUrl(u.profile_pic)} alt="" className="w-5 h-5 rounded-full object-cover" />
                      ) : (
                        <span className="w-5 h-5 rounded-full bg-cyan-500 text-white text-[10px] flex items-center justify-center font-semibold">{(u.name || "?").charAt(0)}</span>
                      )}
                      <span className="text-[11px] text-gray-700 whitespace-nowrap">{u.id === user?.id ? "You" : u.name}</span>
                    </button>
                  ))}
                </div>
              )}

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-3 min-h-0">
                {loading ? (
                  <div className="text-center text-gray-500 py-4">Loading...</div>
                ) : messages.length === 0 ? (
                  <div className="text-center text-gray-500 py-8">
                    <MessageCircle className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                    <p className="text-sm">No messages yet</p>
                    <p className="text-xs">Be the first to say hello!</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {messages.map((msg, index) => {
                      const isOwnMessage = msg.user_id === user?.id;
                      const isSystemMessage = msg.is_system || msg.user_id === "system";
                      const showDate = index === 0 || 
                        formatDate(messages[index - 1].created_at) !== formatDate(msg.created_at);
                      
                      // System message rendering
                      if (isSystemMessage) {
                        const joined = msg.system_type === "member_joined" ? msg.meta || {} : null;
                        return (
                          <div key={msg.id}>
                            {showDate && (
                              <div className="text-center text-xs text-gray-400 my-2">
                                {formatDate(msg.created_at)}
                              </div>
                            )}
                            {joined ? (
                              <div
                                className="mx-auto my-2 max-w-[260px] flex items-center gap-3 px-3 py-2 rounded-xl border border-cyan-200 bg-gradient-to-r from-cyan-50 to-blue-50 cursor-pointer hover:border-cyan-400 transition-colors"
                                data-testid={`member-joined-card-${joined.user_id}`}
                                onClick={() => { if (joined.user_id) { setIsOpen(false); navigate(`/profile/${joined.user_id}`); } }}
                              >
                                {joined.profile_pic ? (
                                  <img src={getImageUrl(joined.profile_pic)} alt="" className="w-9 h-9 rounded-full object-cover ring-2 ring-cyan-300" />
                                ) : (
                                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center text-white text-sm font-semibold ring-2 ring-cyan-300">
                                    {(joined.name || "?").charAt(0)}
                                  </div>
                                )}
                                <div className="min-w-0 text-left">
                                  <p className="text-xs font-semibold text-cyan-800 truncate">{joined.name}</p>
                                  <p className="text-[11px] text-cyan-600 truncate">
                                    Just joined{joined.diving_level ? ` · ${joined.diving_level}` : ""}{joined.country ? ` · ${joined.country}` : ""}
                                  </p>
                                </div>
                                <Anchor className="w-4 h-4 text-cyan-500 ml-auto flex-shrink-0" />
                              </div>
                            ) : (
                              <div className="text-center my-2">
                                <span className="inline-block px-3 py-1.5 bg-cyan-50 text-cyan-700 text-xs rounded-full border border-cyan-200">
                                  <RichContent text={msg.content} className="inline" onNavigate={() => setIsOpen(false)} />
                                </span>
                              </div>
                            )}
                          </div>
                        );
                      }
                      
                      return (
                        <div key={msg.id}>
                          {showDate && (
                            <div className="text-center text-xs text-gray-400 my-2">
                              {formatDate(msg.created_at)}
                            </div>
                          )}
                          <div className={`flex gap-2 ${isOwnMessage ? "flex-row-reverse" : ""}`}>
                            {/* Avatar - clickable to view profile */}
                            <div 
                              className="flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity relative"
                              onClick={() => {
                                setIsOpen(false);
                                navigate(`/profile/${msg.user_id}`);
                              }}
                              title={`View ${msg.user_name}'s profile`}
                            >
                              {msg.user_profile_pic ? (
                                <img 
                                  src={getImageUrl(msg.user_profile_pic)} 
                                  alt="" 
                                  className="w-8 h-8 rounded-full object-cover ring-2 ring-transparent hover:ring-cyan-400 transition-all"
                                />
                              ) : (
                                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center text-white text-xs font-medium ring-2 ring-transparent hover:ring-cyan-400 transition-all">
                                  {msg.user_name?.charAt(0) || "?"}
                                </div>
                              )}
                              {/* Online presence dot */}
                              {onlineUsers.some(u => u.id === msg.user_id) && (
                                <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-green-400 border-2 border-white rounded-full" data-testid={`online-dot-${msg.user_id}`} />
                              )}
                            </div>
                            
                            {/* Message content */}
                            <div className={`flex flex-col ${isOwnMessage ? "items-end" : "items-start"} max-w-[75%]`}>
                              {/* Username above bubble - clickable to view profile */}
                              <p 
                                className={`text-xs mb-0.5 cursor-pointer hover:underline ${isOwnMessage ? "text-gray-400" : "text-gray-500"}`}
                                onClick={() => {
                                  setIsOpen(false);
                                  navigate(`/profile/${msg.user_id}`);
                                }}
                              >
                                {msg.user_name}
                              </p>
                              
                              <div
                                className={`px-3 py-2 rounded-lg ${
                                  msg.is_deleted
                                    ? "bg-gray-100 text-gray-400 italic"
                                    : isOwnMessage
                                    ? "bg-cyan-500 text-white"
                                    : "bg-gray-100 text-gray-900"
                                }`}
                              >
                                {/* Message Image */}
                                {msg.image_url && !msg.is_deleted && (
                                  <img 
                                    src={msg.image_url.startsWith('/api') ? `${BACKEND_URL}${msg.image_url}` : msg.image_url}
                                    alt=""
                                    className="max-w-full max-h-48 rounded-lg mb-2 cursor-pointer"
                                    onClick={() => window.open(msg.image_url.startsWith('/api') ? `${BACKEND_URL}${msg.image_url}` : msg.image_url, '_blank')}
                                  />
                                )}
                                {msg.content && (
                                  <RichContent text={msg.content} className="text-sm break-words" isOwnMessage={isOwnMessage} onNavigate={() => setIsOpen(false)} />
                                )}
                                <p className={`text-xs mt-1 ${isOwnMessage ? "text-white/70" : "text-gray-400"}`}>
                                  {formatTime(msg.created_at)}
                                </p>
                              </div>
                              
                              {/* Reactions with avatars */}
                              {!msg.is_deleted && (
                                <div className={`flex items-center gap-1 mt-1 flex-wrap ${isOwnMessage ? "justify-end" : "justify-start"}`}>
                                  {Object.entries(msg.reactions || {}).map(([emoji, userIds]) => 
                                    renderReactionWithAvatars(emoji, userIds, msg.id)
                                  )}
                                  
                                  {/* Reaction picker button */}
                                  <div className="relative">
                                    <button
                                      onClick={() => setShowReactionPicker(showReactionPicker === msg.id ? null : msg.id)}
                                      className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full text-sm"
                                    >
                                      +
                                    </button>
                                    
                                    {/* Emoji picker */}
                                    {showReactionPicker === msg.id && (
                                      <div 
                                        className={`absolute bottom-full mb-1 p-1.5 bg-white rounded-lg shadow-lg border flex gap-1 z-20 ${isOwnMessage ? "right-0" : "left-0"}`}
                                        style={{ minWidth: 'max-content' }}
                                      >
                                        {REACTION_EMOJIS.map(emoji => (
                                          <button
                                            key={emoji}
                                            onClick={() => handleReaction(msg.id, emoji)}
                                            className="w-7 h-7 flex items-center justify-center hover:bg-gray-100 rounded text-base"
                                          >
                                            {emoji}
                                          </button>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </div>
                )}
              </div>

              {/* Input */}
              <form onSubmit={sendMessage} className="p-2 border-t flex-shrink-0 bg-white relative">
                {/* Typing indicator */}
                {typingUsers.length > 0 && (
                  <div className="px-3 py-1 text-xs text-gray-400 flex items-center gap-1" data-testid="typing-indicator">
                    <span className="flex gap-0.5">
                      <span className="w-1 h-1 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-1 h-1 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-1 h-1 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </span>
                    <span className="italic">
                      {typingUsers.length === 1
                        ? `${typingUsers[0].name} is typing`
                        : typingUsers.length === 2
                          ? `${typingUsers[0].name} and ${typingUsers[1].name} are typing`
                          : `${typingUsers[0].name} and ${typingUsers.length - 1} others are typing`}
                    </span>
                  </div>
                )}
                {/* Mention dropdown */}
                {showMentionDropdown && mentionUsers.length > 0 && (
                  <div className="absolute bottom-full left-0 right-0 mb-1 mx-2 bg-white border rounded-lg shadow-lg z-50 max-h-40 overflow-y-auto">
                    {mentionUsers.map((u) => (
                      <button
                        key={u.id}
                        type="button"
                        onClick={() => selectMention(u)}
                        className="w-full px-3 py-2 flex items-center gap-2 hover:bg-cyan-50 text-left"
                      >
                        {u.profile_pic ? (
                          <img src={getImageUrl(u.profile_pic)} alt="" className="w-6 h-6 rounded-full object-cover" />
                        ) : (
                          <div className="w-6 h-6 rounded-full bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center text-white text-xs">
                            {u.name?.[0] || "?"}
                          </div>
                        )}
                        <span className="text-sm">{u.name}</span>
                      </button>
                    ))}
                  </div>
                )}
                
                <div className="flex gap-2 items-center">
                  <ImageUpload
                    uploadType="chat"
                    compact={true}
                    currentImage={pendingImage}
                    onImageUploaded={(url) => setPendingImage(url)}
                    onImageRemoved={() => setPendingImage(null)}
                  />
                  <input
                    ref={inputRef}
                    type="text"
                    value={newMessage}
                    onChange={handleInputChange}
                    onFocus={() => {
                      // Scroll messages to bottom when input is focused on mobile
                      if (isMobile && messagesEndRef.current) {
                        setTimeout(() => {
                          messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
                        }, 300);
                      }
                    }}
                    onKeyDown={(e) => {
                      // Handle escape to close mention dropdown
                      if (e.key === 'Escape' && showMentionDropdown) {
                        setShowMentionDropdown(false);
                      }
                    }}
                    placeholder="Type a message... (@ to mention)"
                    className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-sm"
                    data-testid="chat-input"
                  />
                  <button
                    type="submit"
                    disabled={!newMessage.trim() && !pendingImage}
                    className="px-3 py-2 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="send-message-btn"
                  >
                    <Send className="w-4 h-4" />
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      )}
    </>
  );

  // Render via portal to document.body for consistent stacking context
  return createPortal(chatContent, document.body);
};

export default FloatingChat;
